import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@saucelabs/ds-typography';
import { Toggle } from '@saucelabs/ds-inputs';
import {
  constants as appStorageConstants,
  records as appStorageRecords,
} from '@saucelabs/sl-module-app-storage';

import SwitchInfoTooltip from './SwitchInfoTooltip';
import { selectors as groupsSelectors } from '../../group';
import selectors from '../selectors';
import operations from '../operations';

const { APP_STORAGE_KIND } = appStorageConstants;

const Resigning: React.FC = () => {
  const dispatch = useDispatch<ReduxDispatcher>();
  const settings = useSelector(
    groupsSelectors.currentGroupSettingsSelector,
  ) as appStorageRecords.AppStorageGroupSettingsIos;
  const currentGroup = useSelector(groupsSelectors.currentGroupSelector);
  const isLoading = useSelector(selectors.isSettingsLoadingSelector);
  const kind = useSelector(groupsSelectors.currentGroupKindSelector);

  const resigningEnabled = settings?.resigning_enabled ?? false;

  const toggleResigningEnabled = useCallback((enabled: boolean) => {
    if (currentGroup) {
      void dispatch(operations.updateSettings({
        groupId: currentGroup.id,
        originalValues: {
          resigning_enabled: resigningEnabled,
        },
        settingsName: 'instrumentation',
        updatedValues: {
          resigning_enabled: enabled,
        },
        warningText: enabled ? undefined : 'You cannot run live or automated tests on public devices when this option is disabled.',
      }));
    }
  }, [currentGroup, dispatch, resigningEnabled]);

  const toggleSubSetting = useCallback((settingsKey: string, enabled: boolean) => {
    if (currentGroup) {
      void dispatch(operations.updateSettings({
        groupId: currentGroup.id,
        originalValues: {
          resigning: {
            [settingsKey]: settings?.resigning?.[settingsKey] ?? false,
          },
        },
        settingsName: settingsKey.replace(/_/g, ' '),
        updatedValues: {
          resigning: {
            ...settings.resigning,
            [settingsKey]: enabled,
          },
        },
      }));
    }
  }, [currentGroup, dispatch, settings?.resigning]);

  // Resigning is a iOS only feature
  if (kind !== APP_STORAGE_KIND.IOS) return null;

  return (
    <>
      <div className="flex justify-between mb-1">
        <div className="flex">
          <Typography className="text-high-emphasis font-semibold">
            {/* Calling it "Instrumentation" here for consistency between Android and iOS apps */}
            Instrumentation
          </Typography>
          <SwitchInfoTooltip
            title="Enables re-signing of iOS apps, which is required to run tests in the real device cloud. Re-signed apps can also use advanced features, like Image Injection. Disabling this option can cause application installation failures."
          />
        </div>
        <Toggle
          data-qa="app-management-settings-resigning-setting-enabled"
          isDisabled={isLoading}
          isSelected={resigningEnabled}
          onChange={toggleResigningEnabled}
        />
      </div>

      <div className="flex justify-between w-full mb-1 pl-3">
        <div className="flex">
          <Typography className="text-high-emphasis font-semibold">
            Network Capture
          </Typography>
          <SwitchInfoTooltip
            title="Enables you to capture network packets during tests to see all HTTP request and responses."
          />
        </div>
        <Toggle
          data-qa="app-management-settings-resigning-setting-network_capture"
          isDisabled={isLoading || !resigningEnabled}
          isSelected={settings?.resigning?.network_capture ?? false}
          onChange={(enabled) => toggleSubSetting('network_capture', enabled)}
        />
      </div>

      <div className="flex justify-between mb-1 pl-3">
        <div className="flex">
          <Typography className="text-high-emphasis font-semibold">
            Image Injection
          </Typography>
          <SwitchInfoTooltip
            title="Enables you to mimic camera behavior when testing applications by letting you upload an image and present it to the application as if it was read by the device camera."
          />
        </div>
        <Toggle
          data-qa="app-management-settings-resigning-setting-image_injection"
          isDisabled={isLoading || !resigningEnabled}
          isSelected={settings?.resigning?.image_injection ?? false}
          onChange={(enabled) => toggleSubSetting('image_injection', enabled)}
        />
      </div>

      <div className="flex justify-between mb-1 pl-3">
        <div className="flex">
          <Typography className="text-high-emphasis font-semibold">
            System Alerts Delay
          </Typography>
          <SwitchInfoTooltip
            title="Delay system alerts, such as asking for permission to access the camera, to prevent app crashes at startup."
          />
        </div>
        <Toggle
          data-qa="app-management-settings-resigning-setting-sys_alerts_delay"
          isDisabled={isLoading || !resigningEnabled}
          isSelected={settings?.resigning?.sys_alerts_delay ?? false}
          onChange={(enabled) => toggleSubSetting('sys_alerts_delay', enabled)}
        />
      </div>

      <div className="flex justify-between mb-1 pl-3">
        <div className="flex">
          <Typography className="text-high-emphasis font-semibold">
            Biometrics Interception
          </Typography>
          <SwitchInfoTooltip
            title="Allows you to choose authentication options if your mobile app requires a biometric authentication, either Touch ID or Face ID."
          />
        </div>
        <Toggle
          data-qa="app-management-settings-resigning-setting-biometrics"
          isDisabled={isLoading || !resigningEnabled}
          isSelected={settings?.resigning?.biometrics ?? false}
          onChange={(enabled) => toggleSubSetting('biometrics', enabled)}
        />
      </div>

      <div className="flex justify-between mb-1 pl-3">
        <div className="flex">
          <Typography className="text-high-emphasis font-semibold">
            Group Folder Redirect
          </Typography>
          <SwitchInfoTooltip
            title="Enables you to use your app's private app container directory instead of the shared app group container directory. When your app gets resigned, the shared directory is not accessible."
          />
        </div>
        <Toggle
          data-qa="app-management-settings-resigning-setting-group_directory"
          isDisabled={isLoading || !resigningEnabled}
          isSelected={settings?.resigning?.group_directory ?? false}
          onChange={(enabled) => toggleSubSetting('group_directory', enabled)}
        />
      </div>
    </>
  );
};

export default Resigning;
